import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import ScrollRevealText from "../../components/ScrollRevealText";
import LogoLoop from "../../components/LogoLoop";
import { Stack, Button } from "../../components";
import { ContentGrid, ContentCard } from "../../components/shared";
import ExperienceModal from "../../components/ExperienceModal";
import LightRays from "../../components/LightRays";
import { projectsService, articlesService, imageTrailsService } from "../../services";
import { activeCo } from "../../App";
import "./About.css";

const VALID_SKILLS = ['manage', 'design', 'animate', 'code'];
const isDesktop = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

export const About = () => {
  const location = useLocation();
  const coParam = (new URLSearchParams(location.search).get('co'))?.toLowerCase() || activeCo;
  const defaultCardRef = useRef(isDesktop() ? 'design' : null);
  const [activeCard, setActiveCard] = useState(defaultCardRef.current);
  const [trailConfig, setTrailConfig] = useState(null);

  useEffect(() => {
    if (!coParam) return;
    imageTrailsService.getAllImageTrails().then(trails => {
      const match = trails.find(t => t.name?.toLowerCase() === coParam);
      if (match) {
        setTrailConfig(match);
        if (isDesktop() && match.skill && VALID_SKILLS.includes(match.skill)) {
          defaultCardRef.current = match.skill;
          setActiveCard(match.skill);
        }
      }
    }).catch(() => {});
  }, [coParam]);

  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const experienceButtonRef = useRef(null);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jakartaTime, setJakartaTime] = useState("");
  const principleRefs = useRef([]);
  const principlesHeaderRef = useRef(null);
  const principlesSectionRef = useRef(null);
  const principlesBackgroundRef = useRef(null);
  const heroNameBackRef = useRef(null);
  const heroNameFrontRef = useRef(null);
  const ornamentTopRef = useRef(null);
  const ornamentBottomRef = useRef(null);
  const ctaCircleRef = useRef(null);
  const ctaTlRef = useRef(null);
  const expCircleRef = useRef(null);
  const expTlRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projects, articles] = await Promise.all([
          projectsService.getAllProjects(),
          articlesService.getPublishedArticles(),
        ]);

        setFeaturedProjects(projects.slice(0, 3));
        setRecentArticles(articles.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const updateJakartaTime = () => {
      const now = new Date();
      const jakartaTime = now.toLocaleString("en-US", {
        timeZone: "Asia/Jakarta",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      setJakartaTime(`${jakartaTime} · Jakarta`);
    };

    updateJakartaTime();
    const interval = setInterval(updateJakartaTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Scroll animation for hero names fade and principles
  useEffect(() => {
    const handleScroll = () => {
      // Handle hero name fade out on scroll
      const scrollY = window.scrollY;
      const fadeDistance = 500; // Distance to complete fade (adjust as needed)

      if (heroNameBackRef.current && heroNameFrontRef.current) {
        // Calculate opacity based on scroll (1 at top, 0 at fadeDistance)
        const opacity = Math.max(0, 1 - scrollY / fadeDistance);
        heroNameBackRef.current.style.opacity = opacity;
        heroNameFrontRef.current.style.opacity = opacity;
      }

      // Handle ornament parallax scroll
      if (ornamentTopRef.current && ornamentBottomRef.current) {
        // Top-right ornament scrolls down slower (0.3x speed)
        const topTransform = scrollY * 0.3;
        ornamentTopRef.current.style.transform = `translateY(${topTransform}px)`;

        // Bottom-left ornament scrolls up faster (negative value, -0.6x speed)
        // Using negative value makes it move up relative to scroll
        const bottomTransform = scrollY * -0.3;
        ornamentBottomRef.current.style.transform = `translateY(${bottomTransform}px)`;
      }

      // Handle principles background parallax scroll
      if (principlesBackgroundRef.current && principlesSectionRef.current) {
        const rect = principlesSectionRef.current.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const sectionScroll = scrollY - sectionTop;

        // Move background up as user scrolls with rotation
        const backgroundTransform = sectionScroll * 0.2;
        principlesBackgroundRef.current.style.transform = `translateX(-50%) translateY(${backgroundTransform}px) rotate(90deg)`;
      }

      // Handle principle cards fade animation
      principleRefs.current.forEach((card) => {
        if (!card) return;

        const rect = card.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const navBarHeight = 80; // Navigation bar height
        const headerHeight = principlesHeaderRef.current
          ? principlesHeaderRef.current.offsetHeight
          : 200;

        // Calculate card position relative to viewport
        const cardTop = rect.top;
        const cardCenter = cardTop + rect.height / 2;

        // Define the visible zone (center area of viewport)
        const visibleZoneTop = navBarHeight + headerHeight + 100; // Below nav + sticky header with buffer
        const visibleZoneBottom = windowHeight - 100; // Near bottom of viewport

        // Calculate fade zones for smoother transitions
        const fadeZoneHeight = 150; // Distance over which fade happens
        const topFadeStart = visibleZoneTop + fadeZoneHeight;
        const bottomFadeStart = visibleZoneBottom - fadeZoneHeight;

        // Determine if card is in visible zone
        const isInCenterZone =
          cardCenter >= topFadeStart && cardCenter <= bottomFadeStart;

        // Calculate opacity and scale based on position
        let opacity = 1;
        let scale = 1;

        if (cardCenter < topFadeStart) {
          // Card is approaching/passing top boundary
          const distance = topFadeStart - cardCenter;
          const fadeProgress = Math.min(distance / fadeZoneHeight, 1);
          opacity = 1 - fadeProgress;
          scale = 1 - fadeProgress * 0.15; // Scale from 1 to 0.85
        } else if (cardCenter > bottomFadeStart) {
          // Card is approaching/passing bottom boundary
          const distance = cardCenter - bottomFadeStart;
          const fadeProgress = Math.min(distance / fadeZoneHeight, 1);
          opacity = 1 - fadeProgress;
          scale = 1 - fadeProgress * 0.15; // Scale from 1 to 0.85
        }

        // Apply smooth transitions
        card.style.opacity = opacity;
        card.style.transform = `scale(${scale})`;

        // Add/remove visibility classes
        if (isInCenterZone) {
          card.classList.add("visible");
          card.classList.remove("hidden");
        } else {
          card.classList.remove("visible");
          card.classList.add("hidden");
        }
      });

      // Handle sticky header behavior
      if (principlesHeaderRef.current && principlesSectionRef.current) {
        const sectionRect =
          principlesSectionRef.current.getBoundingClientRect();
        const lastCard =
          principleRefs.current[principleRefs.current.length - 1];
        const navBarHeight = 80;

        if (lastCard) {
          const lastCardRect = lastCard.getBoundingClientRect();
          const headerHeight = principlesHeaderRef.current.offsetHeight;

          // Check if we've scrolled past the last card
          if (lastCardRect.bottom <= navBarHeight + headerHeight) {
            principlesHeaderRef.current.style.opacity = "0";
          } else if (sectionRect.top <= navBarHeight) {
            principlesHeaderRef.current.style.opacity = "1";
            principlesHeaderRef.current.classList.add("scrolled");
          } else {
            principlesHeaderRef.current.style.opacity = "1";
            principlesHeaderRef.current.classList.remove("scrolled");
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // CTA pill button animation
  useEffect(() => {
    const circle = ctaCircleRef.current;
    if (!circle?.parentElement) return;
    const btn = circle.parentElement;
    const { width: w, height: h } = btn.getBoundingClientRect();
    const R = ((w * w) / 4 + h * h) / (2 * h);
    const D = Math.ceil(2 * R) + 2;
    const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
    circle.style.width = `${D}px`;
    circle.style.height = `${D}px`;
    circle.style.bottom = `-${delta}px`;
    gsap.set(circle, { xPercent: -50, scale: 0, transformOrigin: `50% ${D - delta}px` });
    const label = btn.querySelector('.cta-label');
    const labelHover = btn.querySelector('.cta-label-hover');
    if (label) gsap.set(label, { y: 0 });
    if (labelHover) gsap.set(labelHover, { y: Math.ceil(h + 100), opacity: 0 });
    const tl = gsap.timeline({ paused: true });
    const ease = 'power3.out';
    tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' }, 0);
    if (label) tl.to(label, { y: -(h + 8), duration: 2, ease, overwrite: 'auto' }, 0);
    if (labelHover) tl.to(labelHover, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
    ctaTlRef.current = tl;
  }, []);

  // Experience button pill animation
  useEffect(() => {
    const circle = expCircleRef.current;
    if (!circle?.parentElement) return;
    const btn = circle.parentElement;
    const { width: w, height: h } = btn.getBoundingClientRect();
    const R = ((w * w) / 4 + h * h) / (2 * h);
    const D = Math.ceil(2 * R) + 2;
    const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
    circle.style.width = `${D}px`;
    circle.style.height = `${D}px`;
    circle.style.bottom = `-${delta}px`;
    gsap.set(circle, { xPercent: -50, scale: 0, transformOrigin: `50% ${D - delta}px` });
    const label = btn.querySelector('.exp-pill-label');
    const labelHover = btn.querySelector('.exp-pill-label-hover');
    if (label) gsap.set(label, { y: 0 });
    if (labelHover) gsap.set(labelHover, { y: Math.ceil(h + 100), opacity: 0 });
    const tl = gsap.timeline({ paused: true });
    const ease = 'power3.out';
    tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' }, 0);
    if (label) tl.to(label, { y: -(h + 8), duration: 2, ease, overwrite: 'auto' }, 0);
    if (labelHover) tl.to(labelHover, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
    expTlRef.current = tl;
  }, []);

  const handleExpEnter = () => {
    const tl = expTlRef.current;
    if (tl) tl.tweenTo(tl.duration(), { duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
  };

  const handleExpLeave = () => {
    const tl = expTlRef.current;
    if (tl) tl.tweenTo(0, { duration: 0.2, ease: 'power3.out', overwrite: 'auto' });
  };

  const handleCtaEnter = () => {
    const tl = ctaTlRef.current;
    if (tl) tl.tweenTo(tl.duration(), { duration: 0.3, ease: 'power3.out', overwrite: 'auto' });
  };

  const handleCtaLeave = () => {
    const tl = ctaTlRef.current;
    if (tl) tl.tweenTo(0, { duration: 0.2, ease: 'power3.out', overwrite: 'auto' });
  };

  // Company logos data with individual height adjustments for visual balance
  const companyLogos = [
    { src: "/company logo/ahm.png", alt: "AHM", title: "AHM", height: "42px" },
    {
      src: "/company logo/altrovis.png",
      alt: "Altrovis",
      title: "Altrovis",
      height: "32px",
    },
    {
      src: "/company logo/flip.png",
      alt: "Flip",
      title: "Flip",
      height: "55px",
    },
    {
      src: "/company logo/kiansantang.png",
      alt: "Kian Santang",
      title: "Kian Santang",
      height: "50px",
    },
    { src: "/company logo/lps.png", alt: "LPS", title: "LPS", height: "50px" },
    {
      src: "/company logo/medco.png",
      alt: "Medco",
      title: "Medco",
      height: "50px",
    },
    { src: "/company logo/mpm.png", alt: "MPM", title: "MPM", height: "32px" },
    {
      src: "/company logo/pgnsaka.png",
      alt: "PGN Saka",
      title: "PGN Saka",
      height: "48px",
    },
    { src: "/company logo/pml.png", alt: "PML", title: "PML", height: "48px" },
    {
      src: "/company logo/pratiwi.png",
      alt: "Pratiwi",
      title: "Pratiwi",
      height: "50px",
    },
    {
      src: "/company logo/tinc.png",
      alt: "TINC",
      title: "TINC",
      height: "45px",
    },
    {
      src: "/company logo/utpe.png",
      alt: "UTPE",
      title: "UTPE",
      height: "48px",
    },
  ];

  // Reading stack images data
  const readingImages = [
    {
      id: 1,
      img: "/reading section/winner.jpg",
    },
    {
      id: 2,
      img: "/reading section/read.jpg",
    },
    {
      id: 3,
      
      img: "/reading section/collabs.jpg",
    },
    {
      id: 4,
      
        img: "/reading section/speak.jpg",
    },
    // {
    //   id: 5,
    //   img: "/reading section/KEVN0652.jpg",
    // },
    // {
    //   id: 6,
    //   img: "/reading section/KEVN1392.jpg",
    // },
  ];

  return (
    <main className="edwin-about-page">
      {/* Hero Section */}
      <section className="edwin-about-hero">
        <img
          ref={ornamentTopRef}
          className="edwin-about-hero-ornament"
          src={"/ornaments/object1.png"}
          alt=""
          aria-hidden="true"
        />
        <img
          ref={ornamentBottomRef}
          className="edwin-about-hero-ornament bottom-left"
          src={"/ornaments/object1.png"}
          alt=""
          aria-hidden="true"
        />
        <img
          ref={heroNameBackRef}
          className="edwin-about-hero-name back"
          src={"/back-name.svg"}
          alt=""
          aria-hidden="true"
        />
        <img
          ref={heroNameFrontRef}
          className="edwin-about-hero-name front"
          src={"/front-name.svg"}
          alt=""
          aria-hidden="true"
        />
        <div className="edwin-about-hero-subtitle">
          <p>
            A human passionate about designing experiences that solve real
            problems, delight users, and exceed expectations.
          </p>
        </div>
        <div className="edwin-about-wrapper">
          <div className="edwin-about-hero-layout">
            <div className="edwin-about-photo">
              <img
                className="edwin-about-photo-front"
                src={"/ed-about.png"}
                alt="Edwin Riwaldo"
                data-cursor-text
              />
            </div>
            <div className="edwin-about-info">
              <div className="edwin-about-description">
                <p data-cursor-text>
                  Over 9 years in field of software and design, from client driven to
                  product driven, I've seen how powerful design can be when it
                  brings clarity, trust, and humanity into people's lives. Now
                  I'm a Design Manager in a financial app, on a mission to lead
                  teams in building products that truly matter, while helping
                  each designer grow and reach their best potential.
                </p>
              </div>
              <div className="edwin-about-experience-button-wrapper">
                <button
                  ref={experienceButtonRef}
                  className="edwin-about-experience-button"
                  onClick={() => setIsExperienceModalOpen(true)}
                  onMouseEnter={handleExpEnter}
                  onMouseLeave={handleExpLeave}
                >
                  <span className="exp-pill-circle" ref={expCircleRef} aria-hidden="true" />
                  <span className="exp-pill-stack">
                    <span className="exp-pill-label">See My Experiences</span>
                    <span className="exp-pill-label-hover" aria-hidden="true">See My Experiences</span>
                  </span>
                </button>
              </div>
              <div className="edwin-about-logos">
                <LogoLoop
                  logos={companyLogos}
                  speed={32}
                  direction="left"
                  logoHeight={32}
                  gap={40}
                  pauseOnHover
                  scaleOnHover
                  fadeOut
                  fadeOutColor="var(--bg-primary)"
                  ariaLabel="Companies I've worked with"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="edwin-about-quote">
        <div className="edwin-about-wrapper">
          <div className="edwin-about-quote-container">
            <div className="edwin-about-quote-text">
              <ScrollRevealText>
                Do not follow where the path may lead. Go instead where there is
                no path, and leave a trail.
              </ScrollRevealText>
            </div>
            <p className="edwin-about-quote-author">Ralph Waldo Emerson</p>
          </div>
        </div>
      </section>

      {/* Projects and Articles Section */}
      <section className="edwin-about-section">
        <div className="edwin-about-wrapper">
          {/* Projects */}
          <div className="edwin-about-subsection">
            <div className="edwin-about-section-header">
              <h2 className="edwin-about-section-title" data-cursor-text>
                Fragments of my Journey
              </h2>
              <p className="edwin-about-section-description" data-cursor-text>
                Projects that reflect my thought, my process, and the lessons
                I’ve learned along the way.
              </p>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                Loading projects...
              </div>
            ) : (
              <>
                <ContentGrid>
                  {featuredProjects.map((project) => (
                    <ContentCard
                      key={project.id}
                      variant="project"
                      linkTo={`/projects/${project.id}`}
                      featuredImage={project.featuredImage}
                      title={project.title}
                      description={project.description}
                      year={project.year}
                      tags={project.technologies || []}
                    />
                  ))}
                </ContentGrid>

                <div style={{ textAlign: "center", marginBottom: "4rem" }}>
                  <Button as={Link} to="/projects" variant="outline">
                    See More Projects
                    <ArrowRight size={20} />
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Articles */}
          <div className="edwin-about-subsection">
            <div className="edwin-about-section-header">
              <h2 className="edwin-about-section-title" data-cursor-text>
                Seeds of Thought
              </h2>
              <p className="edwin-about-section-description" data-cursor-text>
                Ideas, lessons, and reflections planted to grow and inspire
                beyond myself.
              </p>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                Loading articles...
              </div>
            ) : (
              <>
                <ContentGrid>
                  {recentArticles.map((article) => (
                    <ContentCard
                      key={article.id}
                      variant="article"
                      linkTo={`/articles/${article.id}`}
                      featuredImage={article.featuredImage}
                      title={article.title}
                      description={article.excerpt}
                      metaItems={[
                        {
                          icon: <Calendar size={14} />,
                          text: article.publishDate
                            ? new Date(...article.publishDate.split('-').map((v, i) => i === 1 ? v - 1 : Number(v))).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : article.createdAt?.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                        },
                        {
                          icon: <Clock size={14} />,
                          text: `${Math.ceil(
                            (article.content?.split(" ").length || 200) / 200
                          )} min read`,
                        },
                      ]}
                      tags={article.tags || []}
                    />
                  ))}
                </ContentGrid>

                <div style={{ textAlign: "center" }}>
                  <Button as={Link} to="/articles" variant="outline">
                    See More Articles
                    <ArrowRight size={20} />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* The Craft I Carry Section */}
      <section className="edwin-about-craft">
        <div className="edwin-about-wrapper">
          <h2 className="edwin-about-title" data-cursor-text>
            The Craft I Carry
          </h2>
          <p className="edwin-about-craft-subtitle" data-cursor-text>
            Not just skills, but disciplines that shape how I think, lead, and
            create.
          </p>
          <div className="edwin-about-craft-grid">
            {/* <div className="edwin-about-craft-card" data-cursor-text>
              <div className="edwin-about-craft-card-inner">
                <div className="edwin-about-craft-card-front">
                  <div className="edwin-about-craft-card-content">
                    <h3>Manage</h3>
                    <p>
                      I lead not by control, but by creating an environment where
                      creativity and purpose move together.
                    </p>
                  </div>
                  <div className="edwin-about-craft-card-image">
                  </div>
                </div>
              </div>
            </div> */}
            {[
              { key: 'manage', label: 'Manage', desc: 'I lead not by control, but by creating an environment where creativity and purpose move together.', src: 'https://res.cloudinary.com/dfepxn7mz/video/upload/v1774396321/leader_hp2xw2.mp4' },
              { key: 'design', label: 'Design', desc: 'Translating ideas into experiences that resonate deeply with people.', src: 'https://res.cloudinary.com/dfepxn7mz/video/upload/v1774396321/design_fiyjg7.mp4' },
              { key: 'animate', label: 'Animate', desc: 'Infusing products with motion that speaks louder than words.', src: 'https://res.cloudinary.com/dfepxn7mz/video/upload/v1774396325/animated_zwxcjm.mp4' },
              { key: 'code', label: 'Code', desc: 'Writing the language that transforms vision into reality.', src: 'https://res.cloudinary.com/dfepxn7mz/video/upload/v1774396321/code_oa0ybq.mp4' },
            ].map(({ key, label, desc, src }) => (
            <div
              key={key}
              className={`edwin-about-craft-card${activeCard === key ? ' active' : ''}`}
              data-cursor-text
              onMouseEnter={() => isDesktop() && setActiveCard(key)}
              onMouseLeave={() => isDesktop() && setActiveCard(defaultCardRef.current)}
            >
              <div className="edwin-about-craft-card-inner">
                <div className="edwin-about-craft-card-front">
                  <div className="edwin-about-craft-card-content">
                    <h3>{label}</h3>
                    <p>{desc}</p>
                  </div>
                  <div className="edwin-about-craft-card-image">
                    <video
                      src={src}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                    />
                  </div>
                </div>
              </div>
            </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Principles Section */}
      <section className="edwin-about-principles" ref={principlesSectionRef}>
        <img
          ref={principlesBackgroundRef}
          className="edwin-about-principles-background"
          src={"/ornaments/object1.png"}
          alt=""
          aria-hidden="true"
        />
        <div
          className="edwin-about-principles-header"
          ref={principlesHeaderRef}
        >
          <h2 className="edwin-about-title" data-cursor-text>
            My Principles
          </h2>
          <p className="edwin-about-principles-subtitle" data-cursor-text>
            The core beliefs that guide my work and shape how I approach every
            challenge.
          </p>
        </div>
        <div className="edwin-about-principles-stack">
          <div
            className="edwin-about-principle-card"
            data-principle="1"
            ref={(el) => (principleRefs.current[0] = el)}
          >
            <div className="edwin-about-principle-content">
              <div className="edwin-about-principle-number">01</div>
              <h3 className="edwin-about-principle-title">
                Never stop learning
              </h3>
              <p className="edwin-about-principle-description">
                When we stop learning, we stop evolving. Curiosity keeps us
                awake, it's how we stay young in spirit and open in mind. Keep
                exploring, keep questioning. There's always something new
                waiting to shape you.
              </p>
            </div>
          </div>

          <div
            className="edwin-about-principle-card"
            data-principle="2"
            ref={(el) => (principleRefs.current[1] = el)}
          >
            <div className="edwin-about-principle-content">
              <div className="edwin-about-principle-number">02</div>
              <h3 className="edwin-about-principle-title">Speak the truth</h3>
              <p className="edwin-about-principle-description">
                It's better to be honest than to be politely unclear. The truth
                isn't always easy, but it builds trust and clears the air. Say
                what you mean with kindness — clarity is one of the greatest
                gifts you can give.
              </p>
            </div>
          </div>

          <div
            className="edwin-about-principle-card"
            data-principle="3"
            ref={(el) => (principleRefs.current[2] = el)}
          >
            <div className="edwin-about-principle-content">
              <div className="edwin-about-principle-number">03</div>
              <h3 className="edwin-about-principle-title">Show, don't tell</h3>
              <p className="edwin-about-principle-description">
                Actions always speak louder than slides or speeches. I'd rather
                build something and let it show what I mean. Making,
                experimenting, and showing the work is how I tell my story.
              </p>
            </div>
          </div>

          <div
            className="edwin-about-principle-card"
            data-principle="4"
            ref={(el) => (principleRefs.current[3] = el)}
          >
            <div className="edwin-about-principle-content">
              <div className="edwin-about-principle-number">04</div>
              <h3 className="edwin-about-principle-title">Lead by example</h3>
              <p className="edwin-about-principle-description">
                Leadership isn't about titles; it's about presence. I try to
                live the values I want to see in others. When you walk the talk,
                people listen without you having to say much.
              </p>
            </div>
          </div>

          <div
            className="edwin-about-principle-card"
            data-principle="5"
            ref={(el) => (principleRefs.current[4] = el)}
          >
            <div className="edwin-about-principle-content">
              <div className="edwin-about-principle-number">05</div>
              <h3 className="edwin-about-principle-title">
                Work smarter, not harder
              </h3>
              <p className="edwin-about-principle-description">
                Hard work matters, but smart focus changes everything. It's not
                about doing more, it's about doing what matters most. When you
                learn to scale your effort and use your energy wisely, growth
                follows naturally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section 2 - hidden */}

      {/* Reading Section */}
      <section className="edwin-about-reading">
        <div className="edwin-about-wrapper">
          <div className="edwin-about-reading-layout">
            <div className="edwin-about-reading-content">
              <h2 className="edwin-about-title" data-cursor-text>
                Grow through learning, live through sharing
              </h2>
              <div className="edwin-about-reading-description">
                <p data-cursor-text>
                  Reading has always been my way of growing. It opens my mind,
                  shapes my thoughts, and reminds me how vast the world truly
                  is. But what I love even more is sharing what I learn with
                  others. Knowledge finds its meaning when it’s passed on. It
                  keeps me grounded and gives me purpose, a quiet reminder that
                  the best kind of person is the one who brings value to others.
                  I try to live through learning and by being useful to others,
                  even in small ways.
                </p>
              </div>
            </div>
            <div className="edwin-about-reading-stack">
              <Stack
                randomRotation={true}
                sensitivity={180}
                sendToBackOnClick={false}
                cardDimensions={{ width: 300, height: 300 }}
                cardsData={readingImages}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="edwin-about-contact-section">
        <div className="edwin-about-contact-background">
          <LightRays
            raysOrigin="top-center"
            raysColor="#00ff00"
            raysSpeed={0.5}
            lightSpread={0.8}
            rayLength={1.5}
            followMouse={true}
            mouseInfluence={0.1}
            noiseAmount={0.05}
            fadeDistance={1.0}
          />
        </div>
        <div className="edwin-about-contact-content">
          <div className="edwin-about-wrapper">
            <div className="edwin-about-contact-hero">
              <div className="edwin-about-contact-time">{jakartaTime}</div>
              <h1 className="edwin-about-contact-title" data-cursor-text>
                {trailConfig?.ctaHeading || 'Great story starts with a simple hello.'}
              </h1>
              <p className="edwin-about-contact-description" data-cursor-text>
                {trailConfig?.ctaSubtext || 'I believe the best ideas come from honest conversations and shared curiosity. If you\'re exploring design, purpose, or personal growth — let\'s talk, learn, and grow together.'}
              </p>
              <Link
                to="/contact"
                className="edwin-about-contact-cta"
                onMouseEnter={handleCtaEnter}
                onMouseLeave={handleCtaLeave}
              >
                <span className="cta-hover-circle" ref={ctaCircleRef} aria-hidden="true" />
                <span className="cta-label-stack">
                  <span className="cta-label">Contact Me</span>
                  <span className="cta-label-hover" aria-hidden="true">Contact Me</span>
                </span>
              </Link>
              <div className="edwin-about-copyright">©2026 Edwin Riwaldo.</div>
            </div>
          </div>
        </div>
      </section>

      <ExperienceModal
        isOpen={isExperienceModalOpen}
        onClose={() => setIsExperienceModalOpen(false)}
        buttonRef={experienceButtonRef}
      />
    </main>
  );
};
